/**
 * lazyWithReload — React.lazy that self-heals stale-deploy chunk errors.
 *
 * When a new build ships while a tab is open, the loaded index.html references
 * chunk hashes that no longer exist on the server. The next route navigation
 * triggers a dynamic import that 404s ("Failed to fetch dynamically imported
 * module"), React surfaces it to the nearest error boundary, and the user sees
 * a scary "Something went wrong loading this page" — fixed only by a manual
 * refresh that pulls the fresh manifest.
 *
 * This wrapper catches that failure at the import promise itself (before any
 * boundary sees it) and forces ONE reload to fetch the current index.html, so
 * the navigation just works after a blink. A sessionStorage timestamp guards
 * against reload loops: if a genuinely broken chunk keeps failing, we reload at
 * most once per window and then let the error propagate to the boundary.
 */
import { lazy, type ComponentType } from 'react';
import { reloadFreshBypassingSW } from './force-fresh-reload';

const RELOAD_KEY = 'wp:chunk-reload-at';
const RELOAD_WINDOW_MS = 20_000;

function isChunkLoadError(err: unknown): boolean {
  const text = String((err as { message?: string })?.message ?? err ?? '');
  return /dynamically imported module|Loading chunk [\d]+ failed|ChunkLoadError|Importing a module script failed|error loading dynamically imported module|Failed to fetch dynamically/i.test(
    text,
  );
}

// Mirror React.lazy's own generic (ComponentType<any>) so per-route prop types
// survive the wrapper — narrowing to ComponentType<unknown> would erase props of
// lazy components that take them (e.g. modals with open/onClose).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (typeof window !== 'undefined' && isChunkLoadError(err)) {
        try {
          const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? '0');
          if (!Number.isFinite(last) || Date.now() - last > RELOAD_WINDOW_MS) {
            window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
            // Bypass the (stale) service worker — a plain reload would be re-served
            // the same stale shell and the chunk would 404 again.
            reloadFreshBypassingSW();
            // Reload is in flight — return a never-settling promise so React
            // keeps showing the Suspense fallback instead of flashing an error.
            return new Promise<{ default: T }>(() => {});
          }
        } catch {
          /* sessionStorage unavailable — fall through and rethrow */
        }
      }
      throw err;
    }),
  );
}
